import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { enrollmentService, courseService } from '../services';
import {
  ArrowLeft, PlayCircle, CheckCircle, Clock, FileText,
  Upload, Loader2, AlertCircle, BookOpen,
} from 'lucide-react';

// ─── Tab pill ──────────────────────────────────────────────────────────────
function Tab({ label, count, isActive, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200
        ${isActive ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
    >
      {label}
      <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold
        ${isActive ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-500'}`}>
        {count}
      </span>
    </button>
  );
}

// ─── Lecture row ───────────────────────────────────────────────────────────
function LectureCard({ lecture }) {
  return (
    <div
      data-testid={`lecture-${lecture.session_number}`}
      className="bg-white rounded-2xl border border-slate-200/60 px-5 py-4 flex items-center gap-4 hover:shadow-md hover:border-blue-200 transition-all duration-200"
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold
        ${lecture.is_watched ? 'bg-green-100 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
        {lecture.is_watched ? <CheckCircle size={17} /> : <span>{lecture.session_number}</span>}
      </div>

      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm leading-snug ${lecture.is_watched ? 'text-slate-500' : 'text-slate-900'}`}>
          Session {lecture.session_number}: {lecture.title}
        </p>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={11} /> {lecture.duration}</span>
          {lecture.is_watched && <span className="text-xs text-green-600 font-medium">Watched</span>}
        </div>
      </div>

      <button
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold flex-shrink-0 transition-all
          ${lecture.is_watched
            ? 'border border-slate-200 text-slate-500 hover:bg-slate-50'
            : 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm'}`}
      >
        <PlayCircle size={13} />
        {lecture.is_watched ? 'Re-watch' : 'Watch'}
      </button>
    </div>
  );
}

// ─── Homework card ─────────────────────────────────────────────────────────
function HomeworkCard({ hw, onSubmit }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(hw.is_submitted);
  const [submittedAt, setSubmittedAt] = useState(hw.submitted_at || '');

  const TYPE_ICON = { video: '🎬', essay: '📝', project: '🔨', reflection: '💭', quiz: '❓' };

  const handleSubmit = async () => {
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 900));
    const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    setSubmitted(true);
    setSubmittedAt(today);
    setSubmitting(false);
    onSubmit?.(hw.id);
  };

  return (
    <div
      data-testid={`hw-card-${hw.id}`}
      className={`bg-white rounded-2xl border p-5 transition-all duration-200
        ${submitted ? 'border-green-200/80' : 'border-slate-200/60 hover:border-blue-200'}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0 text-xl">
          {TYPE_ICON[hw.type] || '📋'}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4 className="font-semibold text-sm text-slate-900">{hw.title}</h4>
            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full capitalize">{hw.type}</span>
          </div>
          {hw.description && (
            <p className="text-xs text-slate-500 leading-relaxed">{hw.description}</p>
          )}
          {submitted && submittedAt && (
            <p className="text-xs text-green-600 mt-1.5 font-medium flex items-center gap-1">
              <CheckCircle size={11} /> Submitted {submittedAt}
            </p>
          )}
          {hw.feedback && submitted && (
            <div className="mt-2 bg-blue-50 rounded-xl px-3 py-2 text-xs text-blue-700 leading-relaxed">
              <span className="font-semibold">Feedback: </span>{hw.feedback}
            </div>
          )}
        </div>

        <div className="flex-shrink-0">
          {submitted ? (
            <div className="flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1.5 rounded-xl text-xs font-semibold">
              <CheckCircle size={12} /> Submitted
            </div>
          ) : (
            <button
              data-testid={`hw-submit-${hw.id}`}
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-1.5 rounded-xl text-xs font-semibold transition-all shadow-sm"
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────
export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate     = useNavigate();

  const [enrollment, setEnrollment]     = useState(null);
  const [detail, setDetail]             = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [activeTab, setActiveTab]       = useState('lectures');
  const [hwSubmitted, setHwSubmitted]   = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [courses, courseDetail] = await Promise.all([
        enrollmentService.getCoachingCourses(),
        courseService.getCourseDetail(courseId),
      ]);
      const enr = courses.find(c => c.course_id === courseId);
      setEnrollment(enr || null);
      setDetail(courseDetail);
      setHwSubmitted(courseDetail.homework?.filter(h => h.is_submitted).length ?? 0);
    } catch {
      setError('Failed to load course. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
    </div>
  );

  if (error || !enrollment) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 border border-red-200 text-center max-w-sm shadow-sm">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-slate-700 font-medium mb-4">{error || 'Course not found.'}</p>
        <button onClick={() => navigate('/my-coaching')} className="px-6 py-2.5 bg-blue-500 text-white rounded-full text-sm font-semibold hover:bg-blue-600">
          Back to Coaching
        </button>
      </div>
    </div>
  );

  const lectures   = detail?.lectures  || [];
  const homework   = detail?.homework  || [];
  const watched    = lectures.filter(l => l.is_watched).length;
  const isComplete = enrollment.status === 'completed';

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Back */}
        <button
          data-testid="course-detail-back-btn"
          onClick={() => navigate('/my-coaching')}
          className="flex items-center gap-1.5 text-blue-500 text-sm font-semibold hover:text-blue-700 transition-colors mb-6"
        >
          <ArrowLeft size={15} /> Back to My Coaching
        </button>

        {/* Hero banner */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-7 mb-6 text-white shadow-lg">
          <div className="absolute -right-8 -top-8 w-44 h-44 bg-white/5 rounded-full" />
          <div className="absolute right-6 -bottom-6 w-28 h-28 bg-white/5 rounded-full" />

          <div className="relative z-10">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <p className="text-blue-200 text-xs font-bold uppercase tracking-widest mb-1">
                  {enrollment.program_name}
                </p>
                <h1 className="text-xl font-bold font-outfit leading-tight">{enrollment.name}</h1>
                {enrollment.participant_name && (
                  <p className="text-blue-200 text-sm mt-1">For: {enrollment.participant_name}</p>
                )}
              </div>
              <span className={`flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-full capitalize
                ${isComplete ? 'bg-white/20 text-white' : 'bg-green-400/30 text-green-100'}`}>
                {enrollment.status?.replace('_', ' ')}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3">
                <p className="text-blue-200 text-xs mb-1">Lectures watched</p>
                <p className="font-bold text-lg leading-none">
                  {watched}<span className="text-blue-200 text-sm font-normal">/{lectures.length}</span>
                </p>
                <div className="w-full bg-white/20 rounded-full h-1.5 mt-2">
                  <div
                    className="bg-white h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${lectures.length ? (watched / lectures.length) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3">
                <p className="text-blue-200 text-xs mb-1">Assignments done</p>
                <p className="font-bold text-lg leading-none">
                  {hwSubmitted}<span className="text-blue-200 text-sm font-normal">/{homework.length}</span>
                </p>
                <div className="w-full bg-white/20 rounded-full h-1.5 mt-2">
                  <div
                    className="bg-white h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${homework.length ? (hwSubmitted / homework.length) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 mb-6 bg-white rounded-2xl p-1.5 border border-slate-200/60 shadow-sm w-fit">
          <Tab label="Lectures" count={lectures.length} isActive={activeTab === 'lectures'} onClick={() => setActiveTab('lectures')} testId="tab-lectures" />
          <Tab label="Homework" count={homework.length} isActive={activeTab === 'homework'} onClick={() => setActiveTab('homework')} testId="tab-homework" />
        </div>

        {/* Lectures */}
        {activeTab === 'lectures' && (
          <div data-testid="lectures-list" className="space-y-2.5">
            {lectures.length === 0 ? (
              <div className="bg-white rounded-3xl border border-slate-200/60 p-12 text-center shadow-sm">
                <BookOpen size={32} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No lectures available yet.</p>
              </div>
            ) : lectures.map(lec => <LectureCard key={lec.id} lecture={lec} />)}
          </div>
        )}

        {/* Homework */}
        {activeTab === 'homework' && (
          <div data-testid="homework-list" className="space-y-3">
            {homework.length === 0 ? (
              <div className="bg-white rounded-3xl border border-slate-200/60 p-12 text-center shadow-sm">
                <FileText size={32} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No assignments yet.</p>
              </div>
            ) : homework.map(hw => (
              <HomeworkCard
                key={hw.id}
                hw={hw}
                onSubmit={() => setHwSubmitted(p => p + 1)}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
